<p class="br-text-muted br-small ti-lede" *ngIf="rememberedFromLastTime">
  These columns are matched the way you matched them last time.
</p>

<!-- A stop, not a caution. A required field with no column fails every row in
     the file for one reason, so the plan built from it would be entirely red
     and tell the person nothing they could act on. -->
<div class="br-alert br-alert--danger br-mb-2 ti-error"
     *ngIf="requiredWithoutColumn.length" data-test="required-unmatched">
  {{ requiredWithoutColumn.length === 1 ? 'This field is' : 'These fields are' }}
  needed for every row, and
  {{ requiredWithoutColumn.length === 1 ? 'has' : 'have' }} no column yet:
  <span class="ti-fields">{{ requiredWithoutColumnLabels }}</span>.
  Choose one for each to go on.
</div>

<!-- ONE ROW PER FIELD OF THIS TABLE, our side on the left, the file on the
     right, and the arrow between them saying which way the data moves. The
     radio group on the right is the whole decision: a column of the file, the
     table's default, one typed value for every row, or nothing. -->
<table class="ti-pairs" data-test="field-rows">
  <thead>
    <tr>
      <th scope="col">In this table</th>
      <th scope="col" class="ti-arrow ti-arrow--head" aria-hidden="true">&#10229;</th>
      <th scope="col">In your file</th>
    </tr>
  </thead>
  <tbody>
    <tr *ngFor="let row of fields; trackBy: trackByField" class="ti-pair"
        [attr.data-field]="row.field"
        [attr.data-source]="row.source"
        [attr.data-how]="row.how">

      <!-- A field no source feeds wears the unmatched mark, same colour as
           the file's unused columns below: one colour, both sides, so the
           shape of the mismatch is visible without reading. -->
      <td class="ti-pair-file"
          [class.ti-unmatched]="row.source === 'blank' && !row.overridable">
        {{ row.label }}
        <span class="ti-required" *ngIf="row.required" title="Required">*</span>
      </td>

      <td class="ti-arrow" aria-hidden="true">&larr;</td>

      <td class="ti-pair-ours">
        <span class="ti-source">
          <!-- Radios rather than a dropdown: four choices, and the one in
               force is the thing a person scans a column of rows for. A closed
               select shows one and hides three, so telling "reads a column"
               from "left blank" down twenty rows meant reading twenty
               sentences; laid out flat, the answer is the position of the
               filled circle.

               They were segmented tiles first, and a tile that is one of four
               is still shaped like a button - something you press to make
               something happen, rather than one value of a single answer. A
               radio group says the exclusivity itself, and the native control
               brings arrow-key walking and the checked state with it. -->
          <span class="ti-choices" role="radiogroup"
                [attr.aria-label]="'Source for ' + row.label">
            <label class="ti-choice">
              <input type="radio" class="ti-choice-input"
                     [name]="'ti-source-' + row.field"
                     [checked]="row.source === 'column'"
                     (change)="chooseSource(row.field, 'column')" />
              <span class="ti-choice-text">A column</span>
            </label>
            <!-- THE SYSTEM'S OWN ANSWER, and the way back to it. Disabled
                 where it matched nothing, which is a fact about this file
                 rather than a refusal, so the title says which. -->
            <label class="ti-choice" [class.ti-choice--off]="!row.auto"
                   [title]="row.auto
                     ? 'What we matched: ' + row.auto
                     : 'We matched no column of your file to this field'">
              <input type="radio" class="ti-choice-input"
                     [name]="'ti-source-' + row.field"
                     [checked]="row.source === 'default'"
                     [disabled]="!row.auto"
                     (change)="chooseSource(row.field, 'default')" />
              <span class="ti-choice-text">Default</span>
            </label>
            <!-- Nothing from the file. Which is not the same as an empty
                 field wherever the table has a value of its own to put
                 there, so the one that has one says so. -->
            <label class="ti-choice"
                   [title]="row.defaultValue
                     ? 'Nothing from your file - the table fills this in with '
                       + row.defaultValue
                     : 'Nothing from your file'">
              <input type="radio" class="ti-choice-input"
                     [name]="'ti-source-' + row.field"
                     [checked]="row.source === 'blank'"
                     (change)="chooseSource(row.field, 'blank')" />
              <span class="ti-choice-text">{{
                row.overridable ? 'Skip' : 'Blank' }}</span>
            </label>
            <label class="ti-choice">
              <input type="radio" class="ti-choice-input"
                     [name]="'ti-source-' + row.field"
                     [checked]="row.source === 'value'"
                     (change)="chooseSource(row.field, 'value')" />
              <span class="ti-choice-text">Fixed value</span>
            </label>
          </span>

          <!-- Which column, shown only while the source IS a column. Its
               options are the file's own headers, nothing else. -->
          <select class="br-form-select br-form-select--sm ti-field-column ti-source-pick"
                  *ngIf="row.source === 'column'"
                  [ngModel]="row.header ?? ''"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="chooseColumn(row.field, $event)"
                  [attr.aria-label]="'Column for ' + row.label">
            <option value="">&mdash; pick a column &mdash;</option>
            <option *ngFor="let header of headers" [value]="header">
              {{ header }}
            </option>
          </select>

          <span class="ti-auto ti-source-pick" *ngIf="row.source === 'default'"
                data-test="auto-column">{{ row.auto }}</span>

          <input type="text" class="br-form-control br-form-control--sm ti-source-pick"
                 *ngIf="row.source === 'value'"
                 [ngModel]="row.value"
                 [ngModelOptions]="{ standalone: true }"
                 (ngModelChange)="setValue(row.field, $event)"
                 [attr.aria-label]="'Value for ' + row.label"
                 placeholder="Value for every row" />

          <!-- WHY THIS ROW IS DIFFERENT, at the end of the controls it is
               about rather than beside the field name. It is a sentence about
               what the choice on the right will do, and in the left column it
               sat against names it says nothing about, holding that column
               open on every row of the table for the sake of two of them. -->
          <span class="ti-field-note br-small ti-note--calm ti-field-note--after"
                *ngIf="row.overridable">
            {{ row.recalculated ? 'calculated' : 'read-only' }} by default
            &mdash; imported only if you choose a column
          </span>
        </span>

        <!-- Only where there is something to say that the controls do not
             already say: the evidence behind a machine-made pairing. Gated on
             the source as well as the pairing, because the row now keeps the
             column it is pointed at while the source is something else - and
             "we guessed this match" is a lie over a field set to Blank.
             A remembered match says nothing here: the banner above already
             says the whole mapping came back from last time, and repeating it
             on every row was the same sentence many times over. -->
        <span class="ti-field-note br-small ti-note--warn"
              *ngIf="readsColumn(row) && row.how === 'fuzzy'">
          We guessed this match &mdash; check it is the column you meant.
        </span>
        <span class="ti-field-note br-small ti-note--warn"
              *ngIf="row.overridable && row.recalculated && row.source === 'column'">
          The next recalculation will overwrite what you import.
        </span>
      </td>
    </tr>
  </tbody>
</table>

<!-- THE FILE'S COLUMNS NOTHING READS: the other side of the mismatch, in the
     same colour. Each says what it is and, where a choice is possible, how to
     make it. -->
<details class="ti-missing" open *ngIf="unused.length" data-test="unused-columns">
  <summary class="ti-missing-title">
    Columns in your file that are not imported ({{ unused.length }})
  </summary>
  <!-- BY WHAT CAN BE DONE ABOUT THEM, with the sentence said once for the
       class rather than once per column. Eight unrecognised columns used to
       carry eight copies of one paragraph, which is read once and skipped
       after that - and skipping it is how the one line that differs from the
       other seven goes unnoticed. -->
  <div class="ti-unused-group"
       *ngFor="let group of unusedGroups; trackBy: trackByKind"
       [attr.data-kind]="group.kind">
    <p class="ti-unused-title">
      {{ group.title }} ({{ group.columns.length }})
    </p>
    <p class="br-small ti-note--calm ti-unused-why">{{ group.sentence }}</p>
    <ul class="ti-unused">
      <li *ngFor="let column of group.columns; trackBy: trackByHeader"
          [attr.data-header]="column.header">
        <span class="ti-unmatched">{{ column.header }}</span>
        <!-- The arrow points at the field of THIS table, the same way it does
             in the pairs above. There the field is on the left and it points
             left; here it is on the right and it points right. -->
        <span class="ti-unused-field" *ngIf="column.ourLabel">
          &rarr; {{ column.ourLabel }}
        </span>
      </li>
    </ul>
  </div>
</details>

<div class="ti-footer">
  <label class="ti-remember">
    <input type="checkbox" [checked]="remember"
           (change)="setRemember($any($event.target).checked)"
           data-test="remember-mapping" />
    Match a file like this the same way next time
  </label>

  <!-- The same idea as the tick, made available NOW. The tick saves on Apply,
       which loses the work of somebody who settles twenty columns and then
       decides not to import today.

       A FILE, and the ellipsis says a dialog is coming - which is the whole
       change here. This was "Save this mapping" over a name box, and it kept
       the mapping on the account without ever saying so, so the reasonable
       reading was that a file had been written somewhere and lost. -->
  <span class="ti-footer-actions">
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
            [disabled]="busy" (click)="save.emit()" data-test="save-mapping">
      Save to a file&hellip;
    </button>
    <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
            [disabled]="busy" (click)="load.emit()" data-test="load-mapping">
      Load from a file&hellip;
    </button>
  </span>
</div>
<p class="br-small ti-note--calm ti-footer-notice" *ngIf="notice"
   data-test="mapping-notice">{{ notice }}</p>
